import React, { useMemo, memo, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import Literal from 'components/literal';
import { AuthenticationActions, ImplementationActions } from 'actions';
import { t } from 'utils/getLiteral';
import ImplementationTable from './components/ImplementationTable';
import { Button, Input, Select } from '@Sage/hoi-poi-ui';
import { Core } from 'managers';
import { ExportImplementations } from '@Sage/ssm-web5';

import './styles.scss';

function mapStateToProps({ implementations, authentication, permissions }) {
    return {
        list: implementations.list,
        listLoading: implementations.listLoading,
        licenses: implementations.licenses || [],
        user: authentication?.user,
        permissions: permissions?.list?.length
            ? permissions?.list
            : Core.cacheManager.getPermissionsStore(),
    };
}

const mapDispatchToProps = (dispatch) => ({
    getImplementations: bindActionCreators(ImplementationActions, dispatch).getImplementations,
    switchImplementation: bindActionCreators(AuthenticationActions, dispatch).switchImplementation,
    logout: bindActionCreators(AuthenticationActions, dispatch).logout,
    createFavorite: bindActionCreators(ImplementationActions, dispatch).createFavorite,
    removeFavorite: bindActionCreators(ImplementationActions, dispatch).removeFavorite,
    getFavorites: bindActionCreators(ImplementationActions, dispatch).getFavorites,
    getLicenses: bindActionCreators(ImplementationActions, dispatch).getLicenses,
});

const FindImplementation = ({
    list = [],
    listLoading,
    licenses = [],
    getImplementations,
    getFavorites,
    getLicenses,
    isModal,
    isModalVisible,
    onChange = () => null,
    switchImplementation,
    logout,
    user,
    createFavorite,
    removeFavorite,
    onNewImplementation = () => null,
    permissions,
    isClearable = true,
}) => {
    const navigate = useNavigate();
    const options = useMemo(
        () => [
            { value: 'name', label: t('cfm_label_name_id') },
            { value: 'email', label: t('cfm_label_email_phone') },
        ],
        [],
    );

    const [searchText, setSearchText] = useState('');
    const [optionSelected, setOptionSelected] = useState(options[0]);
    const [licenseOptionSelected, setLicenseOptionSelected] = useState([]);
    const textSearchRef = useRef();

    const getImplOrFav = useCallback(
        (text, filter, licenseValues) => {
            if (text.length === 0 && (!licenseValues || licenseValues.length === 0)) {
                getFavorites();
            } else {
                getImplementations(text, filter, licenseValues);
            }
        },
        [getFavorites, getImplementations],
    );

    const getLicenseValues = useCallback((selections) => {
        if (!selections || selections.length === 0) return [];
        return selections.map((s) => s.value);
    }, []);

    useEffect(() => {
        const licenseValues = getLicenseValues(licenseOptionSelected);
        getImplOrFav(searchText, optionSelected.value, licenseValues);
    }, [searchText, optionSelected, licenseOptionSelected, getImplOrFav, getLicenseValues]);

    useEffect(() => {
        getLicenses();
    }, [getLicenses]);

    const onSearchTextChange = useCallback((value) => {
        setSearchText(value);
    }, []);

    const onSelectChange = useCallback((selection) => {
        setOptionSelected(selection);
    }, []);

    const onSelectLicenseChange = useCallback((selection) => {
        setLicenseOptionSelected(selection || []);
    }, []);

    const onClickRow = useCallback(
        (implId) => {
            switchImplementation(implId).then(() => {
                onChange(implId);
                navigate('/app/workspace', { replace: true });
            });
        },
        [navigate, onChange, switchImplementation],
    );

    const onToggleFavorite = useCallback(
        (id, isFavorite) => {
            if (isFavorite) {
                removeFavorite(id);
            } else {
                createFavorite(id);
            }
        },
        [createFavorite, removeFavorite],
    );

    const filteredList = useMemo(
        () =>
            searchText.length === 0 &&
            (!licenseOptionSelected || licenseOptionSelected.length === 0)
                ? list.filter((x) => x.favorite)
                : list,
        [list, searchText.length, licenseOptionSelected],
    );

    const filteredLicensesOptionsList = useMemo(() => {
        const hasAllRequiredFields = licenses.every(
            (license) => license && license.licenseCode && license.description,
        );

        if (!hasAllRequiredFields) {
            return [];
        }

        const seen = new Set();
        return licenses
            .filter((license) => {
                if (seen.has(license.licenseCode)) {
                    return false;
                }
                seen.add(license.licenseCode);
                return true;
            })
            .map((license) => ({
                value: license.licenseCode,
                label: license.description,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [licenses]);

    const classesBtns = useMemo(
        () => [
            'find-implementation__btns',
            isModal ? 'find-implementation__btns--modal' : 'find-implementation__btns--page',
        ],
        [isModal],
    );

    const exportFilters = useMemo(
        () => ({
            searchText,
            filter: optionSelected.value,
            license: getLicenseValues(licenseOptionSelected),
        }),
        [searchText, optionSelected.value, licenseOptionSelected, getLicenseValues],
    );

    return (
        <div className="find-implementation">
            {!isModal && (
                <div className="find-implementation__title">
                    <Literal literal="cfm_label_choose_implementation" />
                </div>
            )}
            <div className="find-implementation__search-bar">
                <div className="find-implementation__search-bar__filters">
                    <div className="input-text-search__switch-impl">
                        <div className="input-search-wrapper">
                            <Input
                                placeholder={t('cfm_placeholder_search')}
                                value={searchText}
                                onChange={onSearchTextChange}
                                debounce={700}
                                autoFocus
                                isFullWidth
                            />
                        </div>
                    </div>
                    <div className="search-select d-inline-block">
                        <Select
                            isSearchable={false}
                            value={optionSelected}
                            options={options}
                            onChange={onSelectChange}
                            isClearable={false}
                            isFullWidth
                        />
                    </div>
                    {filteredLicensesOptionsList.length > 0 && (
                        <div className="license-select d-inline-block">
                            <Select
                                isSearchable
                                value={licenseOptionSelected}
                                options={filteredLicensesOptionsList}
                                onChange={onSelectLicenseChange}
                                debounce={1000}
                                isClearable={isClearable}
                                placeholder={t('cfm_label_implementation_license_filter')}
                                isFullWidth
                                isMulti
                            />
                        </div>
                    )}
                </div>
                <div className="find-implementation__search-bar__export">
                    <ExportImplementations filters={exportFilters} />
                </div>
            </div>
            <ImplementationTable
                className="find-implementation__content"
                list={filteredList}
                listLoading={listLoading}
                searchText={searchText}
                onClickRow={onClickRow}
                onToggleFavorite={onToggleFavorite}
            />
            <div className={classesBtns.join(' ')}>
                {!isModal && (
                    <Button
                        className="find-implementation__btns--left"
                        onClick={logout}
                        type="terciary"
                        icon="exitToApp"
                        iconPosition="right"
                    >
                        <Literal literal="cfm_action_logout" />
                    </Button>
                )}
                {permissions?.configuration?.canCreateImplementation && (
                    <Button onClick={onNewImplementation}>
                        <Literal literal="cfm_title_create_new" />
                    </Button>
                )}
            </div>
        </div>
    );
};

FindImplementation.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({})),
    listLoading: PropTypes.bool,
    licenses: PropTypes.arrayOf(PropTypes.shape({})),
    getImplementations: PropTypes.func.isRequired,
    switchImplementation: PropTypes.func.isRequired,
    createFavorite: PropTypes.func.isRequired,
    removeFavorite: PropTypes.func.isRequired,
    getFavorites: PropTypes.func.isRequired,
    getLicenses: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    isModal: PropTypes.bool,
    isModalVisible: PropTypes.bool,
    onNewImplementation: PropTypes.func,
    isClearable: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(FindImplementation));
